import React, { useState, useEffect } from 'react';
import { 
  Target, Award, TrendingUp, Calendar, Building2, 
  CheckCircle2, AlertCircle, ArrowUpRight, Sparkles, RefreshCw,
  Wallet, DollarSign, ArrowDownLeft, ShieldCheck, History, Clock
} from 'lucide-react';
import { bdAPI } from '../utils/api';

const BDPerformance = () => {
  const [data, setData] = useState({
    month: new Date().toISOString().substring(0, 7),
    target_amount: 1000000,
    achieved_amount: 0,
    percentage: 0,
    agencies_count: 0,
    breakdown: []
  });
  const [commData, setCommData] = useState({
    wallet: {
      diamond_balance: 0,
      monthly_earned_diamonds: 0
    },
    ledger: []
  });
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().substring(0, 7));

  useEffect(() => {
    fetchData(selectedMonth);
  }, [selectedMonth]);

  const fetchData = async (month) => {
    try {
      setLoading(true);
      const [perfRes, commRes] = await Promise.all([
        bdAPI.getPerformance({ month }),
        bdAPI.getCommissions({ month })
      ]);
      if (perfRes.data?.success) {
        setData(perfRes.data.performance);
      }
      if (commRes.data?.success) {
        setCommData({
          wallet: commRes.data.wallet || { diamond_balance: 0, monthly_earned_diamonds: 0 },
          ledger: commRes.data.ledger || []
        });
      }
    } catch (err) {
      console.error("Error fetching BD performance and commissions:", err);
    } finally {
      setLoading(false);
    }
  };

  const remaining = Math.max(0, data.target_amount - data.achieved_amount);

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div>
          <div className="flex items-center gap-2 text-emerald-400 text-xs font-bold uppercase tracking-wider mb-2">
            <Target size={16} />
            <span>Target, Ledger & Commission Performance</span>
          </div>
          <h2 className="text-3xl font-black text-white tracking-tight">BD Performance & Commission Wallet</h2>
          <p className="text-slate-400 text-sm mt-1">
            Real-time monthly achievement tracking, commission earnings, and automated wallet transactions ledger.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <input
            type="month"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="bg-[#0f172a] border border-white/10 text-white px-4 py-3 rounded-2xl text-xs font-bold outline-none focus:border-emerald-500 cursor-pointer"
          />
          <button
            onClick={() => fetchData(selectedMonth)}
            className="p-3 rounded-2xl bg-white/5 hover:bg-white/10 border border-white/10 text-slate-300 hover:text-white transition-colors cursor-pointer"
          >
            <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* Commission Wallet Highlight Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-gradient-to-br from-emerald-950/40 via-[#0f172a] to-[#0b0f19] border border-emerald-500/20 rounded-[28px] p-6 backdrop-blur-xl relative overflow-hidden">
          <div className="flex items-center justify-between mb-4">
            <span className="text-xs font-bold text-emerald-400 uppercase tracking-wider">BD Commission Wallet</span>
            <div className="w-10 h-10 rounded-xl bg-emerald-500/15 text-emerald-400 flex items-center justify-center">
              <Wallet size={20} />
            </div>
          </div>
          <h3 className="text-3xl font-black text-white font-mono">
            {(commData.wallet?.diamond_balance || 0).toLocaleString()} <span className="text-emerald-400 text-lg">💎</span>
          </h3>
          <p className="text-xs text-slate-400 mt-2">Available Diamond Balance in BD Wallet</p>
        </div>

        <div className="bg-gradient-to-br from-indigo-950/40 via-[#0f172a] to-[#0b0f19] border border-indigo-500/20 rounded-[28px] p-6 backdrop-blur-xl relative overflow-hidden">
          <div className="flex items-center justify-between mb-4">
            <span className="text-xs font-bold text-indigo-400 uppercase tracking-wider">Earned This Month</span>
            <div className="w-10 h-10 rounded-xl bg-indigo-500/15 text-indigo-400 flex items-center justify-center">
              <Sparkles size={20} />
            </div>
          </div>
          <h3 className="text-3xl font-black text-white font-mono">
            {(commData.wallet?.monthly_earned_diamonds || 0).toLocaleString()} <span className="text-indigo-400 text-lg">💎</span>
          </h3>
          <p className="text-xs text-slate-400 mt-2">Accrued Commission ({selectedMonth})</p>
        </div>

        <div className="bg-gradient-to-br from-purple-950/40 via-[#0f172a] to-[#0b0f19] border border-purple-500/20 rounded-[28px] p-6 backdrop-blur-xl relative overflow-hidden">
          <div className="flex items-center justify-between mb-4">
            <span className="text-xs font-bold text-purple-400 uppercase tracking-wider">Commission Rate</span>
            <div className="w-10 h-10 rounded-xl bg-purple-500/15 text-purple-400 flex items-center justify-center">
              <ShieldCheck size={20} />
            </div>
          </div>
          <h3 className="text-3xl font-black text-white font-mono">
            22% <span className="text-purple-400 text-sm font-normal">of Agency Commission</span>
          </h3>
          <p className="text-xs text-slate-400 mt-2">Automatic ledger credit upon host completion</p>
        </div>
      </div>

      {/* Target Progress Banner */}
      <div className="relative overflow-hidden bg-gradient-to-br from-[#0f172a] via-[#111c35] to-[#0a232e] border border-white/10 rounded-[36px] p-8 md:p-10 shadow-2xl">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
          <div className="lg:col-span-2 space-y-6">
            <div className="flex items-center gap-3">
              <span className="px-3.5 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-bold uppercase tracking-wider">
                Month: {data.month}
              </span>
              <span className="text-xs text-slate-400">
                {data.percentage >= 100 ? '🎉 Goal Achieved!' : `${data.percentage}% Completed`}
              </span>
            </div>

            <div>
              <div className="flex items-baseline justify-between mb-3">
                <span className="text-4xl md:text-5xl font-black text-white tracking-tight">
                  {data.achieved_amount?.toLocaleString()}
                </span>
                <span className="text-sm font-semibold text-slate-400">
                  Target: {data.target_amount?.toLocaleString()} Coins
                </span>
              </div>

              {/* Progress bar */}
              <div className="w-full bg-white/5 rounded-full h-3.5 p-0.5 border border-white/10 overflow-hidden">
                <div
                  className="bg-gradient-to-r from-emerald-500 via-teal-400 to-cyan-400 h-full rounded-full transition-all duration-1000 shadow-lg shadow-emerald-500/30"
                  style={{ width: `${Math.min(100, data.percentage)}%` }}
                ></div>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-6 pt-2 text-xs font-medium text-slate-400">
              <div className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 rounded-full bg-emerald-400"></div>
                <span>Achieved: <strong className="text-white">{data.achieved_amount?.toLocaleString()}</strong></span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 rounded-full bg-slate-600"></div>
                <span>Remaining: <strong className="text-white">{remaining.toLocaleString()}</strong></span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 rounded-full bg-cyan-400"></div>
                <span>Contributing Agencies: <strong className="text-white">{data.agencies_count}</strong></span>
              </div>
            </div>
          </div>

          {/* Achievement Status Badge Card */}
          <div className="bg-white/5 border border-white/10 rounded-[28px] p-6 text-center backdrop-blur-md">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-tr from-emerald-500/20 to-cyan-500/20 border border-emerald-500/30 flex items-center justify-center text-emerald-400 mx-auto mb-4">
              <Award size={32} />
            </div>
            <h4 className="text-lg font-bold text-white">
              {data.percentage >= 100 ? 'Target Unlocked' : 'Target In Progress'}
            </h4>
            <p className="text-xs text-slate-400 mt-1 max-w-[200px] mx-auto">
              {data.percentage >= 100 
                ? 'Congratulations! BD monthly milestone completed.' 
                : `${remaining.toLocaleString()} more coins needed to fulfill quota.`}
            </p>
          </div>
        </div>
      </div>

      {/* Real-Time Commission Ledger */}
      <div className="bg-[#0f172a] border border-white/10 rounded-[32px] p-6 md:p-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-xl font-bold text-white flex items-center gap-2">
              <History size={20} className="text-emerald-400" />
              <span>Real-Time Commission Ledger</span>
            </h3>
            <p className="text-xs text-slate-400 mt-1">Automatic wallet credit entries from Host & Agency completions</p>
          </div>
          <span className="text-xs font-bold text-emerald-400 bg-emerald-500/10 px-3 py-1.5 rounded-xl border border-emerald-500/20">
            {commData.ledger?.length || 0} Transactions
          </span>
        </div>

        {commData.ledger?.length === 0 ? (
          <div className="p-12 text-center text-slate-400">
            <Clock size={40} className="mx-auto mb-3 opacity-30" />
            <p className="text-sm">No commission transactions recorded yet for this month.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-white/5 text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                  <th className="pb-4">Date & Time</th>
                  <th className="pb-4">Type</th>
                  <th className="pb-4">Source Host / Partner</th>
                  <th className="pb-4">Description</th>
                  <th className="pb-4 text-right">Commission (💎)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5 text-sm">
                {commData.ledger.map((tx) => (
                  <tr key={tx.id} className="hover:bg-white/[0.02] transition-colors">
                    <td className="py-4 text-xs text-slate-400">
                      {tx.created_at ? new Date(tx.created_at).toLocaleString() : 'N/A'}
                    </td>
                    <td className="py-4">
                      <span className="px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-400 text-xs font-bold border border-emerald-500/20">
                        {tx.transaction_type}
                      </span>
                    </td>
                    <td className="py-4">
                      <p className="font-bold text-white text-xs">{tx.source_user_name || 'System Reward'}</p>
                      {tx.source_xoxo_id && (
                        <p className="text-[10px] text-slate-400 font-mono">ID: {tx.source_xoxo_id}</p>
                      )}
                    </td>
                    <td className="py-4 text-xs text-slate-300">
                      {tx.description || 'Target reward commission credit'}
                    </td>
                    <td className="py-4 text-right font-mono font-bold text-emerald-400">
                      +{(Number(tx.amount) || 0).toLocaleString()} 💎
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Agency-wise Target Contributions Breakdown */}
      <div className="bg-[#0f172a] border border-white/10 rounded-[32px] p-6 md:p-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-xl font-bold text-white">Agency Contribution Breakdown</h3>
            <p className="text-xs text-slate-400 mt-1">Individual coin performance for each managed agency</p>
          </div>
          <span className="text-xs font-bold text-emerald-400 bg-emerald-500/10 px-3 py-1.5 rounded-xl border border-emerald-500/20">
            {data.breakdown.length} Agencies
          </span>
        </div>

        {data.breakdown.length === 0 ? (
          <div className="p-12 text-center text-slate-400">
            <Building2 size={40} className="mx-auto mb-3 opacity-30" />
            <p className="text-sm">No agency contributions recorded for this month.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-white/5 text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                  <th className="pb-4">Agency</th>
                  <th className="pb-4">Agency Code</th>
                  <th className="pb-4">Manager</th>
                  <th className="pb-4">Hosts</th>
                  <th className="pb-4">Achieved Coins</th>
                  <th className="pb-4 text-right">Contribution %</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5 text-sm">
                {data.breakdown.map((item) => {
                  const contribPct = data.achieved_amount > 0 
                    ? Math.round((item.achieved_coins / data.achieved_amount) * 100) 
                    : 0;

                  return (
                    <tr key={item.id} className="hover:bg-white/[0.02] transition-colors">
                      <td className="py-4">
                        <div className="font-bold text-white">{item.agency_name}</div>
                      </td>
                      <td className="py-4 font-mono font-bold text-emerald-400 text-xs">
                        #{item.agency_code || '---'}
                      </td>
                      <td className="py-4 text-slate-300">
                        {item.real_name || 'Agency Manager'}
                      </td>
                      <td className="py-4 text-slate-300">
                        {item.hosts_count} Hosts
                      </td>
                      <td className="py-4 font-bold text-white">
                        {Number(item.achieved_coins || 0).toLocaleString()}
                      </td>
                      <td className="py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <span className="text-xs font-bold text-cyan-400">{contribPct}%</span>
                          <div className="w-16 bg-white/5 rounded-full h-1.5 overflow-hidden">
                            <div
                              className="bg-cyan-400 h-full rounded-full"
                              style={{ width: `${contribPct}%` }}
                            ></div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default BDPerformance;
